import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buyerAssignmentsApi } from "@/api/buyers";
import { buyerAllocationKeys, buyerAssignmentKeys, buyerKeys } from "./buyerKeys";

/**
 * Assignment from anywhere — who the caller may hand a buyer to, and the two
 * writes that move a buyer's owner.
 *
 * <p>🔴 <b>Both mutations invalidate THREE prefixes, and all three are needed.</b>
 * A reassign moves the row on the buyers list (and its UNASSIGNED card and
 * stat), removes or restores a row on the assignment queue, and moves the
 * specialists' standings — entitlement is computed from what each person
 * holds, not stored, so nothing narrower reaches it. This is the same blast
 * radius the queue's own per-row assign carries
 * (`BuyerAssignmentQueuePage.tsx:289-291`), for the same reason.</p>
 *
 * <p>The invalidations are AWAITED, as the queue's are, so `mutateAsync`
 * resolves once the list has refetched: a caller that closes a prompt on
 * resolution closes it onto fresh rows rather than onto the old owner for the
 * few hundred milliseconds a refetch takes. Nothing here patches the cache by
 * hand — the server's row is the only truth, and the refetch carries it.</p>
 */

/**
 * The people the caller may hand this buyer to — or, with `null`, a buyer
 * about to be created. Server-resolved (`BuyerSpecialistCandidatesDto`); the
 * client never decides who is offerable.
 */
export function useSpecialistCandidates(
  buyerLeadId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: buyerAssignmentKeys.candidates(buyerLeadId),
    queryFn: () => buyerAssignmentsApi.candidates(buyerLeadId),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Who the caller may hand an EXISTING buyer to — one read per caller, shared by
 * every row that renders a quick-pick (they all use this key). Each row's own
 * `canReassign` / `canUnassign` say whether it may be moved or returned.
 */
export function useHandOffRoster(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: buyerAssignmentKeys.handOffRoster(),
    queryFn: () => buyerAssignmentsApi.handOffRoster(),
    enabled: options?.enabled ?? true,
  });
}

export interface AssignBuyerVariables {
  buyerLeadId: string;
  specialistUserId: string;
  /** Required on this path: an owner hand-off and any reassign of an owned
   *  buyer are recorded against a reason on the grant row. */
  reason: string;
}

export interface UnassignBuyerVariables {
  buyerLeadId: string;
  reason: string;
}

/** Place ONE buyer with ONE specialist. Resolves to the row the server returned. */
export function useAssignBuyer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ buyerLeadId, specialistUserId, reason }: AssignBuyerVariables) =>
      buyerAssignmentsApi.assign(buyerLeadId, { specialistUserId, reason }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: buyerKeys.all });
      await qc.invalidateQueries({ queryKey: buyerAssignmentKeys.all });
      await qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
    },
  });
}

/** Return ONE buyer to the unassigned queue. Resolves to the row the server returned. */
export function useUnassignBuyer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ buyerLeadId, reason }: UnassignBuyerVariables) =>
      buyerAssignmentsApi.unassign(buyerLeadId, { reason }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: buyerKeys.all });
      await qc.invalidateQueries({ queryKey: buyerAssignmentKeys.all });
      await qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
    },
  });
}
